import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { AddBranchInformation, ResturantData } from "@/types/globalTypes";
import { formatMoney } from "@/utils/helpers";
import { AppConfig } from "@/utils/AppConfig";
import { useSelector } from "react-redux";

type AddBranchCardProps = {
  handleRequestBranches: (branchNumbers: number) => void;
  addBranchInformation: AddBranchInformation;
  isRequesting: boolean;
};

function AddBranchCard({
  handleRequestBranches,
  isRequesting,
  addBranchInformation,
}: AddBranchCardProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const description =
    locale === "ar"
      ? addBranchInformation?.addBranchData?.descriptionAR
      : addBranchInformation?.addBranchData?.descriptionEN;

  const currentBranches =
    addBranchInformation?.branchesLimit 

  const ResturantData = useSelector(
    (state: { resturant: { resturantData: ResturantData } }) =>
      state.resturant.resturantData,
  );

  const WithoutPlan = ResturantData?.subscription === null
  const isFreePlan = ResturantData?.subscription?.isFree;

  const validationSchema = Yup.object().shape({
    branchNumbers: Yup.number()
      .min(1, t("subscribtionSettings.minBranches"))
      .max(50, t("subscribtionSettings.maxBranches"))
      .required(t("subscribtionSettings.branchNumbersRequired")),
  });

  const formik = useFormik({
    initialValues: {
      branchNumbers: 1,
    },
    validationSchema,
    onSubmit: (values) => {
      handleRequestBranches(values.branchNumbers);
    },
  });

  return (
    <div className="relative flex flex-col rounded-lg border border-[#BAF3FD] bg-white px-6 py-5 shadow-lg transition-shadow duration-200 hover:shadow-xl">
      <div className="flex items-center justify-between">
        <h4 className="mb-2 text-xl font-semibold text-[#1B2B34]">
          {t("subscribtionSettings.addBranch")}
        </h4>
      </div>

      {/* Price */}

      <div className="mt-2 flex items-center justify-between pe-1 font-semibold">
        <p>{t("subscribtionSettings.BranchPrice")}</p>
        <p dir="rtl">
          {!Number(addBranchInformation?.addBranchData?.price)
            ? t("subscribtionSettings.free")
            : formatMoney(addBranchInformation?.addBranchData?.price) + " "}
          {!Number(addBranchInformation?.addBranchData?.price)
            ? null
            : AppConfig.currency.icon()}
        </p>
      </div>

      {/* Period */}
      <div className="mt-1 flex items-center justify-between pe-1 font-semibold">
        <p>{t("subscribtionSettings.currentBranches")}</p>
        <p>
          {currentBranches} {t("subscribtionSettings.branch")}
        </p>
      </div>

      <hr className="my-3" />

      <p className="text-sm text-gray-700">{description}</p>

      <hr className="mt-3" />

      <form onSubmit={formik.handleSubmit} className="flex h-full flex-col">
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <label
            htmlFor="branches-number"
            className="text-sm font-medium text-gray-700"
          >
            {t("subscribtionSettings.ChooseBranchesNumber")} :
          </label>
          <div className="relative min-w-[100px] max-w-[100px] flex-1">
            <input
              type="number"
              id="branches-number"
              name="branchNumbers"
              value={formik.values.branchNumbers}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={isRequesting || isFreePlan || WithoutPlan}
              className={`h-8 w-full rounded border border-gray-400 text-center text-sm disabled:bg-gray-300 disabled:text-gray-500 `}
            />
          </div>
        </div>
        {formik.touched.branchNumbers && formik.errors.branchNumbers && (
          <div className="mt-1 text-xs text-red-500">
            {formik.errors.branchNumbers}
          </div>
        )}

        <div className="flex-grow"></div>

        <button
          type="submit"
          disabled={isRequesting || isFreePlan || WithoutPlan}
          className="group relative mt-4 w-full rounded-full bg-[#BAF3FD] px-4 py-2 font-semibold text-[#1B2B34] transition-colors hover:bg-[#1B2B34] hover:text-skyblue disabled:cursor-not-allowed disabled:bg-gray-400 disabled:text-gray-600" // Added 'group' class
        >
          {t("subscribtionSettings.addBranch")}
          {(isFreePlan || WithoutPlan) && (
            <>
              {/* Tooltip */}
              <span className="pointer-events-none absolute -top-10 left-1/2 -translate-x-1/2 transform whitespace-nowrap rounded bg-skyblue p-2 text-xs text-sidebar opacity-0 transition-opacity group-hover:opacity-100 capitalize">
                {t("subscribtionSettings.mustSubscribeForAddBranches")}
              </span>
            </>
          )}
        </button>
      </form>
    </div>
  );
}

export default AddBranchCard;
